import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import z from 'zod'
import { toast } from 'sonner'
import {
    Boxes,
    Plus,
    Edit,
    Trash2,
    ToggleLeft,
    ToggleRight,
    Search,
    Check,
} from 'lucide-react'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Badge } from '../components/ui/badge'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '../components/ui/dialog'
import { FormError } from '../components/ui/FormError'
import { Spinner } from '@/app/lib/loader/loader'
import { TableSkeleton } from '../components/ui/table-skeleton'
import { categoryService, Category } from '../services/category.service'
import { ConfirmationModal } from '../components/ui/ConfirmationModal'
import { GlobalPaginator } from '../components/ui/global-paginator'

// ─── Esquemas de validación ─────────────────────────────────────────────────

const categorySchema = z.object({
    name: z.string().min(1, 'El nombre es requerido'),
    description: z.string().optional(),
    is_active: z.boolean(),
})

type CategoryFormValidate = z.infer<typeof categorySchema>

// ─── Component ────────────────────────────────────────────────────────────────

export function Categorias() {
    const [categories, setCategories] = useState<Category[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const [searchQuery, setSearchQuery] = useState('')

    // Paginación
    const [pagination, setPagination] = useState({
        currentPage: 1,
        total: 0,
        perPage: 10,
        lastPage: 1,
        from: 0,
        to: 0,
    })

    // Modals state
    const [showModal, setShowModal] = useState(false)
    const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null)
    const [statusConfirmId, setStatusConfirmId] = useState<number | null>(null)
    const [editingId, setEditingId] = useState<number | null>(null)

    const {
        register,
        handleSubmit,
        watch,
        setValue,
        reset,
        formState: {
            errors,
            isSubmitting,
            isValid,
        }
    } = useForm<CategoryFormValidate>({
        resolver: zodResolver(categorySchema),
        mode: 'onChange',
        reValidateMode: 'onChange',
        defaultValues: {
            name: '',
            description: '',
            is_active: true,
        }
    })

    const isActiveValue = watch('is_active')

    // ─── Handlers ───────────────────────────────────────────────────────────────

    const fetchCategories = async (page = 1, search = searchQuery) => {
        setIsLoading(true)

        try {
            const res = await categoryService.getCategories({
                page,
                per_page: pagination.perPage,
                search,
            })

            const paginator = res.data

            setCategories(paginator.data)

            setPagination({
                currentPage: paginator.current_page,
                total: paginator.total,
                perPage: paginator.per_page,
                lastPage: paginator.last_page,
                from: paginator.from,
                to: paginator.to,
            })
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        fetchCategories(pagination.currentPage, searchQuery)
    }, [pagination.currentPage, searchQuery])

    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value)
        setPagination(prev => ({
            ...prev,
            currentPage: 1,
        }))
    }

    const openCreateModal = () => {
        setEditingId(null)
        reset({
            name: '',
            description: '',
            is_active: true,
        })
        setShowModal(true)
    }

    const openEditModal = (category: Category) => {
        setEditingId(category.id)
        reset({
            name: category.name,
            description: category.description || '',
            is_active: category.is_active,
        })
        setShowModal(true)
    }

    const handleCloseModal = () => {
        setShowModal(false)
        setEditingId(null)
        reset()
    }

    const onSubmit = async (data: CategoryFormValidate) => {
        try {
            const payload = {
                ...data,
            }

            if (editingId) {
                await categoryService.updateCategory(editingId, payload)
                toast.success('Categoría actualizada exitosamente')
            } else {
                await categoryService.createCategory(payload)
                toast.success('Categoría creada exitosamente')
            }
            handleCloseModal()
            fetchCategories()
        } catch (error) {
            toast.error('Ocurrió un error al guardar la categoría')
        }
    }

    // ─── Eliminar Registro  ──────────────────────────────────────
    const executeDelete = async () => {
        if (!deleteConfirmId) return;
        try {
            await categoryService.deleteCategory(deleteConfirmId)
            toast.success('Categoría eliminada exitosamente')
            fetchCategories()
        } catch (error) {
            toast.error("Error", { description: "No se pudo eliminar la categoría." });
        } finally {
            setDeleteConfirmId(null); // Cerramos el modal bonito
        }
    };
    // ─── Cambiar estado de Registro ──────────────────────────────────────
    const executeChangeStatus = async () => {
        if (!statusConfirmId) return;
        try {
            await categoryService.updateStatus(statusConfirmId)
            toast.success('Estado de la categoría actualizado exitosamente')
            fetchCategories()
        } catch (error) {
            toast.error("Error", { description: "No se pudo actualizar el estado de la categoría." });
        } finally {
            setStatusConfirmId(null); // Cerramos el modal bonito
        }
    };

    // ─── Render ──────────────────────────────────────────────────────────────────

    return (
        <div className="space-y-6 p-6">
            {/* Header & Filters */}
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-slate-900">Categorías</h1>
                    <p className="mt-1 text-sm text-slate-600">
                        Administra las categorías de los productos.
                    </p>
                </div>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                        <Input
                            placeholder="Buscar categoría..."
                            value={searchQuery}
                            onChange={handleSearch}
                            className="w-full pl-9 sm:w-64"
                        />
                    </div>
                    <Button
                        onClick={openCreateModal}
                        className="bg-[#3B7D3B] hover:bg-[#2d6a2d] w-full sm:w-auto"
                    >
                        <Plus className="mr-2 h-4 w-4" />
                        Nueva Categoría
                    </Button>
                </div>
            </div>

            {/* Table */}
            {isLoading ? (
                <TableSkeleton
                    headers={['Nombre', 'Descripción', 'Fecha de Actualización', 'Estado', 'Acciones']}
                    columns={['text', 'text-two-lines', 'text', 'badge', 'actions']}
                    rows={5}
                />
            ) : (
                <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
                    <div className="overflow-x-auto">
                        <table className="w-full whitespace-nowrap">
                            <thead className="border-b border-slate-200 bg-slate-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">
                                        Nombre
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">
                                        Descripción
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">
                                        Fecha de Actualización
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">
                                        Estado
                                    </th>
                                    <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-slate-500">
                                        Acciones
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-200">
                                {categories.length > 0 ? (
                                    categories.map((category) => (
                                        <tr key={category.id} className="transition-colors hover:bg-slate-50/50">
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-3">
                                                    <div>
                                                        <p className="font-medium text-slate-900">{category.name}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex flex-col gap-1 text-sm text-slate-600">
                                                    {category.description && (
                                                        <span className="flex items-center gap-1.5">
                                                            {category.description}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-slate-600">
                                                {category.updated_at ? category.updated_at.split('T')[0] : ''}
                                            </td>
                                            <td className="px-6 py-4">
                                                <Badge
                                                    className={
                                                        category.is_active
                                                            ? 'bg-green-100 text-green-700 hover:bg-green-100'
                                                            : 'bg-slate-100 text-slate-700 hover:bg-slate-100'
                                                    }
                                                >
                                                    {category.is_active ? 'Activa' : 'Inactiva'}
                                                </Badge>
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <div className="flex items-center justify-end gap-2">
                                                    <button
                                                        onClick={() => setStatusConfirmId(category.id)}
                                                        title={category.is_active ? 'Desactivar' : 'Activar'}
                                                        className={`rounded p-1.5 transition-colors ${category.is_active
                                                            ? 'text-green-600 hover:bg-green-50'
                                                            : 'text-slate-400 hover:bg-slate-100'
                                                            }`}
                                                    >
                                                        {category.is_active ? (
                                                            <ToggleRight className="h-5 w-5" />
                                                        ) : (
                                                            <ToggleLeft className="h-5 w-5" />
                                                        )}
                                                    </button>
                                                    <button
                                                        onClick={() => openEditModal(category)}
                                                        title="Editar"
                                                        className="rounded p-1.5 text-slate-600 transition-colors hover:bg-slate-100"
                                                    >
                                                        <Edit className="h-4 w-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => setDeleteConfirmId(category.id)}
                                                        title="Eliminar"
                                                        className="rounded p-1.5 text-red-600 transition-colors hover:bg-red-50"
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={5} className="px-6 py-12 text-center">
                                            <Boxes className="mx-auto mb-3 h-8 w-8 text-slate-300" />
                                            <p className="text-slate-500">No se encontraron categorias</p>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    {/* Controles de Paginacion */}
                    {categories.length > 0 && (
                        <GlobalPaginator
                            totalItems={pagination.total}
                            itemsPerPage={pagination.perPage}
                            currentPage={pagination.currentPage}
                            from={pagination.from}
                            to={pagination.to}
                            onPageChange={(page) =>
                                setPagination(prev => ({
                                    ...prev,
                                    currentPage: page,
                                }))
                            }
                        />
                    )}
                </div>
            )}

            {/* ═══════════════════════════════════════════════════════════
          MODAL: Crear / Editar Categoria
      ══════════════════════════════════════════════════════════════ */}
            <Dialog open={showModal} onOpenChange={setShowModal}>
                <DialogContent className="sm:max-w-md">
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2 text-lg font-semibold text-slate-900">
                                <Boxes className="h-5 w-5 text-[#3B7D3B]" />
                                {editingId ? 'Editar Categoria' : 'Nueva Categoria'}
                            </DialogTitle>
                            <DialogDescription>
                                {editingId
                                    ? 'Modifica los datos de la categoria existente.'
                                    : 'Ingresa los datos para registrar una nueva categoria.'}
                            </DialogDescription>
                        </DialogHeader>

                        <div className="space-y-4 py-4">
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-slate-700">
                                    Nombre de la categoria <span className="text-red-500">*</span>
                                </label>
                                <Input
                                    placeholder="Ej. Higiene Personal"
                                    {...register('name')}
                                />
                                <FormError message={errors.name?.message} />
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-medium text-slate-700">
                                    Descripción
                                </label>
                                <Input
                                    placeholder="Descripción de la categoria"
                                    {...register('description')}
                                />
                                <FormError message={errors.description?.message} />
                            </div>

                            <div className="flex items-center gap-2 pt-2">
                                <button
                                    type="button"
                                    role="switch"
                                    aria-checked={isActiveValue}
                                    onClick={() => setValue('is_active', !isActiveValue)}
                                    className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none ${isActiveValue ? 'bg-green-500' : 'bg-slate-200'
                                        }`}
                                >
                                    <span
                                        aria-hidden="true"
                                        className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${isActiveValue ? 'translate-x-2' : '-translate-x-2'
                                            }`}
                                    />
                                </button>
                                <span className="text-sm text-slate-700">Categoria Activa</span>
                            </div>
                        </div>

                        <DialogFooter className="gap-2 sm:justify-end">
                            <Button type="button" variant="outline" onClick={handleCloseModal} disabled={isSubmitting}>
                                Cancelar
                            </Button>
                            <Button
                                type="submit"
                                disabled={isSubmitting}
                                className="bg-[#3B7D3B] hover:bg-[#2d6a2d]"
                            >
                                {isSubmitting ? (
                                    <Spinner className="mr-2 h-4 w-4" size="sm" />
                                ) : (
                                    <Check className="mr-2 h-4 w-4" />
                                )}
                                Guardar
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>


            {/* ═══════════════════════════════════════════════════════════
                      MODALS DE CONFIRMACIÓN
                  ══════════════════════════════════════════════════════════════ */}
            <ConfirmationModal
                isOpen={deleteConfirmId !== null}
                onClose={() => setDeleteConfirmId(null)}
                onConfirm={executeDelete}
                preset="delete"
                itemName="categoria"
            />
            <ConfirmationModal
                isOpen={statusConfirmId !== null}
                onClose={() => setStatusConfirmId(null)}
                onConfirm={executeChangeStatus}
                preset="toggleStatus"
                itemName="categoria"
            />
        </div>
    )
}
